import { AfterViewChecked, Component, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { AppRoutingService } from '@services/app-routing/app-routing.service';
import { AdminHTTPService } from '@services/datasources/admin-http-service';
import { AdminDataSyncService } from '@services/users/admin-data-sync.service';
import { AdminOrganizationListEntryDto } from 'app/@types/dto/admin-organization-list-entry-dto.model';
import { ConfigService } from 'app/app-config.service';
import { Subject, combineLatest, takeUntil } from 'rxjs';
import { FeatureService } from '@services/features/feature.service';
import { LoaderService } from '@services/loader/loader.service';

interface OrganizationListEntry {
    uid: string;
    name: string;
    index: number;
    solutionCount: number;
    initials: string;
}

@Component({
    selector: 'app-dedicated-admin-organisation-list',
    templateUrl: './dedicated-admin-organisation-list.component.html',
    styleUrls: ['./dedicated-admin-organisation-list.component.scss'],
    standalone: false,
})
export class DedicatedAdminOrganisationListComponent implements OnInit, AfterViewChecked, OnDestroy {
    // when main admin instanciate this component, it needs an id. if it is a dedicatedAdmin it does not

    private ngUnsubscribe: Subject<void> = new Subject();
    public isLoaded: boolean = false;
    public isGlobalAdmin: boolean = false;
    clientName = this.configService.getClient.name;

    // Auto-scroll the list to the organisation selected when the console is (re)opened — e.g. after
    // "take control" — so a restored selection isn't lost off-screen. Captured at init and applied
    // once, so it never hijacks scrolling when the user simply clicks an organisation in the list.
    private orgToScrollIntoView?: string;
    private hasScrolledToSelection = false;

    constructor(
        public configService: ConfigService,
        private AdminHttpService: AdminHTTPService,
        private appRouting: AppRoutingService,
        public adminSignals: AdminDataSyncService,
        private featureService: FeatureService,
        private loader: LoaderService,
        private host: ElementRef<HTMLElement>,
    ) {}

    ngOnInit(): void {
        this.orgToScrollIntoView = this.adminSignals.currentSelectedOrgId.getValue();
        this.getData();
        this.isGlobalAdmin =
            this.featureService.hasUiFeatures(['admin_admin'], false) ||
            this.featureService.hasUiFeatures(['admin_admin'], true);
        this.adminSignals.udpateListSubject.pipe(takeUntil(this.ngUnsubscribe)).subscribe((bool) => {
            if (bool) {
                this.getData();
            }
        });
        this.adminSignals.currentSelectedDedicatedAdminId
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(() => this.getData());
    }

    ngAfterViewChecked(): void {
        if (this.hasScrolledToSelection || !this.orgToScrollIntoView) {
            return;
        }
        const selectedRow = this.host.nativeElement.querySelector<HTMLElement>('.pick-row.is-active');
        const container = selectedRow?.closest<HTMLElement>('.org-list__scroll');
        if (!selectedRow || !container) {
            // Still loading: the row isn't in the DOM yet — retry next check. Once the list has
            // loaded without a matching row, the selection isn't in this admin's list, so stop
            // retrying — otherwise we'd query the DOM on every change-detection cycle indefinitely.
            if (this.isLoaded) {
                this.hasScrolledToSelection = true;
            }
            return;
        }
        const rowOffset =
            selectedRow.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop;
        container.scrollTop = rowOffset - container.clientHeight / 2 + selectedRow.clientHeight / 2;
        this.hasScrolledToSelection = true;
    }

    getData() {
        this.adminSignals.loading.next(true);
        this.isLoaded = false;
        combineLatest([
            this.AdminHttpService.getOrganizationList(this.adminSignals.currentSelectedDedicatedAdminId.getValue()),
            this.AdminHttpService.getUnassignedOrganizationList(),
        ])
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe({
                next: ([organizationList, unassignedOrganizationsList]) => {
                    this.organizations = this.mapData(organizationList);
                    this.organizations.sort((org1, org2) => {
                        return org1.name.localeCompare(org2.name);
                    });
                    this.unassignedOrganizations = this.mapData(unassignedOrganizationsList);
                    this.unassignedOrganizations.sort((org1, org2) => {
                        return org1.name.localeCompare(org2.name);
                    });
                    this.applySearch();
                    this.isLoaded = true;
                    this.adminSignals.loading.next(false);
                },
                error: () => {
                    this.organizations = [];
                    this.unassignedOrganizations = [];
                    this.applySearch();
                    this.isLoaded = true;
                    this.adminSignals.loading.next(false);
                },
            });
    }

    mapData(data: AdminOrganizationListEntryDto[]): OrganizationListEntry[] {
        return data.map((p, i) => ({
            ...p,
            name: p.name,
            uid: p.uid ?? '',
            index: i,
            initials: initialsOf(p.name),
        }));
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }

    public organizations: OrganizationListEntry[] = [];
    public unassignedOrganizations: OrganizationListEntry[] = [];
    /** Both lists narrowed to the search, by name. */
    public visibleOrganizations: OrganizationListEntry[] = [];
    public visibleUnassigned: OrganizationListEntry[] = [];
    private searchTerm = '';

    /** The organisation's colour, a chart colour picked by its place in the list. */
    avatarColor(index: number): string {
        const colors = this.configService.brand.chartColors;
        return colors[index % colors.length];
    }

    search(term: string): void {
        this.searchTerm = term;
        this.applySearch();
    }

    routeToOrganisationForm(organization: OrganizationListEntry): void {
        this.adminSignals.currentSelectedOrgId.next(organization.uid);
        this.appRouting.navigateToOganisationForm();
    }

    routeToNewOrganisationForm(): void {
        this.adminSignals.udpateListSubject.next(true);
        this.adminSignals.currentSelectedOrgId.next(undefined);
        this.appRouting.navigateToOganisationForm();
    }

    private applySearch(): void {
        const needle = this.searchTerm.trim().toLocaleLowerCase();
        const matches = (o: OrganizationListEntry): boolean => !needle || o.name.toLocaleLowerCase().includes(needle);
        this.visibleOrganizations = this.organizations.filter(matches);
        this.visibleUnassigned = this.unassignedOrganizations.filter(matches);
    }
}

/** The first letters of an organisation's first two words ("Ben test again" → "BT"), or of its only one. */
function initialsOf(name: string): string {
    const words = (name ?? '').trim().split(/\s+/).filter(Boolean);
    return words
        .slice(0, 2)
        .map((word) => word.charAt(0))
        .join('')
        .toLocaleUpperCase();
}
